import { Link } from 'react-router-dom';
import Button from '@/components/common/Button';
import AdvisorImage from '@/components/common/AdvisorImage';
import StaticPageLayout from '@/components/layout/StaticPageLayout';
import distressedImg from '@/images/distressed.png';
import advisor1Img from '@/images/ryan.png';
import advisor2Img from '@/images/mark_lind.png';
import advisor3Img from '@/images/judd.png';

export default function BlogPostFriendBaseToFanBase() {
  return (
    <StaticPageLayout>
      {/* ============ HERO / HEADER ============ */}
      <section className="relative z-[6] -mt-[50px] bg-[#111] bg-cover bg-center px-6 pb-[calc(100px+50px)] pt-[calc(100px+50px)] text-center md:-mt-[80px] md:px-10 md:pb-[calc(100px+80px)] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_calc(100%-50px),0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]"
      style={{ backgroundImage: `url(${distressedImg})` }}
      >
        <div className="mx-auto max-w-3xl px-6">
          <Link
            to="/blog"
            className="text-sm text-primary-600 font-semibold hover:text-primary-700 transition-colors no-underline"
          >
            ← Back to Blog
          </Link>
          <h1 className="mt-6 font-heading text-[36px] uppercase tracking-[2px] text-white md:text-[60px] leading-[1]">
            HOW TO TURN A FRIEND BASE INTO A FAN BASE
          </h1>
          <p className="mt-4 text-lg text-neutral-500">
            July 2026 · 8 min read
          </p>
        </div>
      </section>

      {/* ============ ARTICLE BODY ============ */}
      <section className="relative -mt-[80px] bg-neutral-200 px-6 pb-[calc(100px+80px)] pt-[calc(100px+80px)] [clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]">
        <div className="mx-auto max-w-3xl">
          <article className="max-w-none">

            {/* ADVISOR NOTE */}
            <p className="text-sm italic text-neutral-500 leading-relaxed">
              Every BandJerry article is shaped by our Band of Advisors: musicians, promoters, bookers, tour managers, merch pros, and scene veterans who have spent years figuring out what actually helps bands grow. We pull from hours of interviews with them and turn their hard-earned expertise into practical insight you can use to make smarter moves.
            </p>

            {/* INTRO */}
            <p className="mt-8 text-lg text-neutral-700 leading-relaxed">
              Every band's earliest supporters are usually friends.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              That's not a problem. It's just how this works. Friends come to the first shows, buy the first shirts, and tell you the set was great even when it absolutely wasn't. They make the room feel less empty while you figure out how to be a band in public.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              But at some point, a loyal friend base becomes a ceiling.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              Even if the room feels alive, playing to the same 30 people every time is not the same as building an audience. It's a group hang with a door charge.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              That is the difference between feeling supported and actually growing. And it's a jump a lot of bands miss.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <AdvisorImage src={advisor1Img} alt="R.A., promoter / booker" size={100} />
              <blockquote className="border-l-4 border-primary-600 bg-neutral-50  p-6 italic text-neutral-700">
              "It's building an audience that's outside of your friend group."
              <span className="mt-2 block text-sm not-italic text-neutral-500">— R.A., promoter / booker</span>
              </blockquote>
            </div>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              Friends come because they know you. Fans come because the band is good and getting better. A friend might buy a shirt to be supportive. A fan wears it because it says something about them. And that's the person who tells someone else, drags a few people to the next show, and starts the domino effect you're actually looking for.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              No more guilting people into coming. No more social posts of quiet desperation asking people to "come out, it would mean a lot."
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              If you're stuck in this rut, you have to ask an honest question: what, if anything, are we giving people that makes them want to be here without being asked?
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              A FRIEND BASE IS NOT FAILURE
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              This is not meant to shittalk friends. Friends are incredibly important to a band's early growth. They're living, breathing proof that someone cares enough to show up, and that what you're doing is worth trying harder at.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              A friend base gives you a place to start. It gives you a room to practice in front of and the first signs of what people respond to.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              But you have to recognize it for what it is.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <AdvisorImage src={advisor2Img} alt="M.L., independent musician / promoter / distro owner" size={100} />
              <blockquote className="border-l-4 border-primary-600 bg-neutral-50  p-6 italic text-neutral-700">
                "You guys are a friend-based band, but you're teetering on the edge of a fan base. Keep going or break up in a year. Your call."
                <span className="mt-2 block text-sm not-italic text-neutral-500">— M.L., independent musician / promoter / distro owner</span>
              </blockquote>
            </div>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              That line was said to a band right when they were starting to take off. It floored them at the time, because they thought they already <em>were</em> big. They were packing the same grimy club every weekend. They knew everyone and everyone knew them.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              But as they realized later, it was the best advice they could have gotten at that point.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Without it, they might have stayed trapped in the friend zone forever. Not unknown. Not bad. Not begging for shows. Just not growing. People liked the band. Some people even loved them. But all that energy ran on personal connection more than actual pull.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              That stage can be exciting. But it's equally dangerous. It can trick you into thinking you have more momentum than you do. A local-band anesthesia that dulls your hustle and makes you feel like you've already arrived when you've barely gotten out of the driveway.
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              DON'T CONFUSE ATTENDANCE WITH MOMENTUM
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              A packed room feels great. A half-empty room can feel great too, if the people in it are giving something back. Just be honest with yourself about what you're looking at.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              If the same friends are at every show, that does not mean you are failing. It means people care. That's not just step one. It's the biggest step there is. But if the audience never extends beyond that same group, stagnation has already started setting in.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              That does not mean the music sucks or it's time to pack it in. It just means your current system has reached its limit and it's time to build outward.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              The question you ask yourself now isn't, "Did people come?" It's "Who came, why, and how do we get more people like them?"
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Is it because they're friends? Friends of friends? Fans of the other band? Did new faces show up because you got more heat on a post or clip? Was this flyer or bill different than the rest? Is someone talking you up?
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Those answers tell you what kind of audience you're working toward, not just the one you have.
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              BUILD WIDER CIRCLES
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              A lot of bands think growth means bigger rooms. Sometimes it does. But most of the time, especially early on, growth is much better visualized as widening circles.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Your first circle is friends. Then friends of friends. Then other bands that play similar clubs and bills. Then people who saw you open for someone else. Then people who came because someone they trust told them to. On and on toward infinity.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              That is how a friend base becomes a fan base. One circle at a time.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              It's not always linear, and it's not always easy to clock while it's happening. But nine out of ten times, this is how organic growth works. Not going viral or some lucky break. Just a bunch of small, iterative proofs that things are headed in the right direction.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <AdvisorImage src={advisor3Img} alt="J.S., tour manager / road manager / merch manager" size={100} />
              <blockquote className="border-l-4 border-primary-600 bg-neutral-50  p-6 italic text-neutral-700">
                "If you can see 15 people to 18 people as growth, that's progress. And progress can be replicated. Expect too much too soon, and you end up bitter. Angry bands can get big. Insane bands can get big. Bitter bands just get progressively more bitter."
                <span className="mt-2 block text-sm not-italic text-neutral-500">— J.S., tour manager / road manager / merch manager</span>
              </blockquote>
            </div>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed">
              Progress is the goal. Not growth at all costs, but growth wherever you can find it.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Don't pretend every small win is a massive success, and don't beat yourself up after every gig that wasn't as great as you hoped. Just notice when something is clicking, even slightly, and learn from it.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Who came back? Who brought someone? Which post got a response? Which band on the bill helped? Which room felt right? What part of the set worked? What did people say after?
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Clock the patterns and use them to inform your next move.
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              MAKE IT EASY FOR FRIENDS TO BRING NEW PEOPLE IN
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Your friends are still part of the story. They'll always be the true believers and most important voices in the room.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              They're also your easiest bridge to people who don't know you yet.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              So don't just ask them to come. Ask them to bring someone who has never seen you. Make the show welcoming to people who are not already in on the joke. Give your friends something easy to explain. Make the bill feel worth inviting people to. Be good to the other bands. Be good to the venue. Be visible in the room. Talk to the people who came out. Remember faces.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              That is how someone moves from "I know this person in the band" to "I love this band."
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              A friend supports your band because they care about you. A fan supports your band because it gives them something to care about.
            </p>

            <h2 className="mt-16 font-heading text-3xl uppercase text-primary-600">
              THE REAL GOAL
            </h2>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Turning a friend base into a fan base will not happen in one move.
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              It happens when you stop asking, "How do we get our people to show up?" and start asking, "How do we make new people care?"
            </p>

            <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
              Stop measuring room size and start paying attention to who came back, who brought someone, who bought something, who posted after the fact, who talked to you after, and who shows up next time without being asked.
            </p>

            <p className="mt-6 text-lg text-neutral-600 leading-relaxed font-semibold text-white">
              Your friends help get the band started; strangers keep it going.
            </p>

          </article>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section className="relative z-[6] -mt-[50px] bg-[#111] bg-cover bg-center px-6 pb-[calc(100px+50px)] pt-[calc(100px+50px)] text-center md:-mt-[80px] md:px-10 md:pb-[calc(100px+80px)] md:pt-[calc(100px+80px)] [clip-path:polygon(0_50px,100%_0,100%_calc(100%-50px),0_100%)] md:[clip-path:polygon(0_80px,100%_0,100%_calc(100%-80px),0_100%)]"
      style={{ backgroundImage: `url(${distressedImg})` }}
      >
        <div className="mx-auto max-w-3xl">
          <h2 className="font-heading text-[36px] uppercase tracking-[2px] text-primary-600 md:text-[60px]">
            READY TO GROW BEYOND YOUR FRIEND GROUP?
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-neutral-600">
            BandJerry helps you find new audiences, build strategic bills, and make moves that expand your reach. Get personalized, actionable marketing tasks tailored to your band's genre, city, and goals.
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-lg font-semibold text-white">
            Your first 3 Moves are free. No credit card required.
          </p>
          <div className="mt-10 flex items-center justify-center">
            <Link to="/signup">
              <Button size="lg">Start Free — Get Your First 3 Moves</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============ RELATED CONTENT / ABOUT ============ */}
      <section className="relative -mt-[80px] bg-neutral-200 px-6 pb-32 pt-[calc(100px+80px)] [clip-path:polygon(0_80px,100%_0,100%_100%,0_100%)]">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-heading text-3xl uppercase tracking-[2px] text-primary-600">
            ABOUT BANDJERRY
          </h2>
          <p className="mt-6 text-lg text-neutral-600">
            BandJerry is a marketing tool built specifically for independent musicians. It generates customized, actionable marketing tasks tailored to your band's goals, genre, and location.
          </p>
          <p className="mt-4 text-lg text-neutral-600">
            Instead of generic advice that could apply to anyone, BandJerry gives you specific moves: real venue contacts, pre-written outreach emails, playlist curator leads, social media strategies, and step-by-step plans you can execute in a single sitting.
          </p>
          <div className="mt-8 flex items-center gap-4">
            <Link to="/about">
              <Button variant="secondary">Learn More About Us</Button>
            </Link>
            <Link to="/blog">
              <Button variant="secondary">Back to Blog</Button>
            </Link>
          </div>
        </div>
      </section>
    </StaticPageLayout>
  );
}
